/**
 * Rax Spend Tracker Component
 *
 * Displays real-time spend information on dashboard and in chat.
 * Shows daily/weekly/monthly breakdown.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type SpendPreferences } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";
import "./cost-trend-chart.js";

interface BudgetStatus {
  usedCents: number;
  limitCents: number;
  remainingCents: number;
  tierLimitCents: number;
  policyLimitCents: number;
  explicitOptUp: boolean;
}

interface SpendData {
  totalCostUsd: number;
  budgetLimitCents: number | null;
  budgetStatus: BudgetStatus | null;
  usageSource?: "database" | "runtime_fallback";
  usageWarning?: string | null;
  lastSyncAt?: string | null;
  sessions: Array<{
    sessionKey: string;
    totalCostUsd: number;
    llmCalls: number;
    toolCalls: number;
  }>;
  usage?: {
    totalCents: number;
    totalCalls: number;
    totalTokensIn: number;
    totalTokensOut: number;
    totalTokensThinking?: number;
    inputCostCents?: number;
    outputCostCents?: number;
    thinkingCostCents?: number;
    byType: Record<string, number>;
    byModel: Record<string, number>;
    bySkill?: Record<string, number>;
  };
  spendPolicy: SpendPreferences | null;
}

@customElement("rax-spend-tracker")
export class RaxSpendTracker extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .spend-container {
        padding: var(--space-6);
      }
      
      .spend-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .spend-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .refresh-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .refresh-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      /* Spend Overview Cards */
      .overview-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }
      
      .overview-card {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .overview-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-2);
      }
      
      .overview-value {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .overview-value.green {
        color: var(--success);
      }
      .overview-value.red {
        color: var(--error);
      }
      
      .overview-subtext {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-top: var(--space-1);
      }
      
      /* Budget Bar */
      .budget-section {
        margin-bottom: var(--space-6);
      }
      
      .budget-label {
        display: flex;
        justify-content: space-between;
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
      }
      
      .budget-bar {
        height: 8px;
        background: var(--bg-secondary);
        border-radius: 4px;
        overflow: hidden;
      }
      
      .budget-fill {
        height: 100%;
        border-radius: 4px;
        transition: width 0.5s ease;
      }
      
      .budget-fill.safe {
        background: var(--success);
      }
      .budget-fill.warning {
        background: #f59e0b;
      }
      .budget-fill.danger {
        background: var(--error);
      }
      
      .budget-alert {
        margin-top: var(--space-3);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        line-height: 1.4;
      }
      
      .budget-alert.warning {
        border: 1px solid rgba(245, 158, 11, 0.5);
        background: rgba(245, 158, 11, 0.12);
        color: #fbbf24;
      }
      
      .budget-alert.danger {
        border: 1px solid rgba(239, 68, 68, 0.55);
        background: rgba(239, 68, 68, 0.14);
        color: #fca5a5;
      }
      
      /* By Model Breakdown */
      .breakdown-section {
        margin-bottom: var(--space-6);
      }
      
      .breakdown-title {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
      }
      
      .breakdown-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        margin-bottom: var(--space-2);
      }
      
      .breakdown-name {
        font-size: var(--font-sm);
        color: var(--text-primary);
        font-weight: 500;
      }
      
      .breakdown-cost {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--rax-yellow);
      }
      
      /* Session List */
      .sessions-title {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
      }
      
      .session-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        margin-bottom: var(--space-2);
        font-size: var(--font-sm);
      }
      
      .session-key {
        color: var(--text-secondary);
        font-family: ui-monospace, monospace;
        font-size: var(--font-xs);
      }
      
      .session-stats {
        display: flex;
        gap: var(--space-3);
        align-items: center;
      }
      
      .session-stat {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
      }
      
      .session-cost {
        font-weight: 600;
        color: var(--rax-yellow);
      }
      
      .policy-section {
        margin-bottom: var(--space-6);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .policy-title {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-3);
      }
      
      .policy-note {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-bottom: var(--space-3);
      }
      
      .policy-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: var(--space-3);
        margin-bottom: var(--space-3);
      }
      
      .policy-field {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .policy-field label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .policy-field input {
        padding: var(--space-2);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-primary);
        font-size: var(--font-sm);
      }
      
      .policy-checkbox {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-bottom: var(--space-3);
      }
      
      .policy-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
      }
      
      .policy-error {
        font-size: var(--font-xs);
        color: var(--error);
      }
      
      .save-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-sm);
        color: var(--rax-black);
        font-size: var(--font-xs);
        font-weight: 700;
        cursor: pointer;
      }
      
      .secondary-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        font-weight: 700;
        cursor: pointer;
      }
      
      .secondary-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .secondary-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      .save-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .loading {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      @media (max-width: 900px) {
        .spend-container {
          padding: var(--space-4);
        }
      
        .policy-actions {
          flex-direction: column;
          align-items: stretch;
        }
      }
      
      @media (max-width: 640px) {
        .spend-container {
          padding: var(--space-3);
        }
      
        .spend-header {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-3);
        }
      
        .overview-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--space-3);
        }
      
        .policy-grid {
          grid-template-columns: minmax(0, 1fr);
        }
      
        .session-item {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }
      
        .session-stats {
          width: 100%;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: var(--space-2);
        }
      }
    `,
  ];

  @property({ type: String })
  mode: "dashboard" | "compact" = "dashboard";

  @state()
  private data: SpendData | null = null;

  @state()
  private loading = true;

  @state()
  private savingPolicy = false;

  @state()
  private policyError: string | null = null;

  @state()
  private capUsdInput = "0.00";

  @state()
  private alertPercentInput = 80;

  @state()
  private explicitOptUpInput = false;

  @state()
  private pauseOnCapInput = false;

  @state()
  private resumingPausedAgents = false;

  // Daily alert settings
  @state()
  private dailyAlertEnabledInput = false;

  @state()
  private dailyAlertLimitUsdInput = "1.00";

  @state()
  private dailyAlertEmailInput = true;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const [usageRes, spendPrefRes] = await Promise.all([
        raxApi.request<any>("GET", "/api/rax/usage"),
        raxApi.getSpendPreferences(),
      ]);

      if (usageRes.success && usageRes.data) {
        const usage = usageRes.data;
        const spendPolicy = spendPrefRes.success ? (spendPrefRes.data ?? null) : null;
        const byModel =
          usage.byModel && !Array.isArray(usage.byModel)
            ? usage.byModel
            : Object.fromEntries(
                (Array.isArray(usage.modelBreakdown) ? usage.modelBreakdown : []).map(
                  (row: any) => [row.model, Number(row.costCents ?? 0)],
                ),
              );
        const budgetStatus: BudgetStatus | null = usage.budget
          ? {
              usedCents: Number(usage.budget.usedCents ?? 0),
              limitCents: Number(usage.budget.limitCents ?? 0),
              remainingCents: Number(usage.budget.remainingCents ?? 0),
              tierLimitCents: Number(usage.budget.tierLimitCents ?? 0),
              policyLimitCents: Number(usage.budget.policyLimitCents ?? 0),
              explicitOptUp: usage.budget.explicitOptUp === true,
            }
          : null;

        if (spendPolicy) {
          this.capUsdInput = (spendPolicy.policyLimitCents / 100).toFixed(2);
          this.alertPercentInput = Math.round(spendPolicy.alertThreshold * 100);
          this.explicitOptUpInput = spendPolicy.explicitOptUp;
          this.pauseOnCapInput = spendPolicy.pauseOnCap === true;
          // Daily alert settings
          this.dailyAlertEnabledInput = spendPolicy.dailyAlertEnabled === true;
          this.dailyAlertLimitUsdInput = (spendPolicy.dailyAlertLimitCents / 100).toFixed(2);
          this.dailyAlertEmailInput = spendPolicy.dailyAlertEmail !== false;
        }

        this.data = {
          totalCostUsd: usage.monthly.totalCents / 100,
          budgetLimitCents:
            Number(usage.budget?.limitCents ?? 0) > 0
              ? Number(usage.budget.limitCents)
              : (spendPolicy?.effectiveLimitCents ?? null),
          budgetStatus,
          usageSource: usage.usageSource === "runtime_fallback" ? "runtime_fallback" : "database",
          usageWarning:
            typeof usage.usageWarning === "string" && usage.usageWarning.trim().length > 0
              ? usage.usageWarning
              : null,
          sessions: (Array.isArray(usage.recentSessions) ? usage.recentSessions : []).map(
            (s: any) => ({
              sessionKey: String(s.sessionId ?? "unknown-session"),
              totalCostUsd: Number(s.totalCents ?? 0) / 100,
              llmCalls: Number(s.llmCalls ?? 0),
              toolCalls: Number(s.toolCalls ?? 0),
            }),
          ),
          usage: {
            totalCents: Number(usage.monthly.totalCents ?? 0),
            totalCalls: Number(usage.monthly.totalCalls ?? 0),
            totalTokensIn: Number(usage.monthly.totalTokensIn ?? 0),
            totalTokensOut: Number(usage.monthly.totalTokensOut ?? 0),
            byType: usage.byType && !Array.isArray(usage.byType) ? usage.byType : {},
            byModel: byModel && !Array.isArray(byModel) ? byModel : {},
          },
          spendPolicy,
        };
      } else {
        this.data = {
          totalCostUsd: 0,
          budgetLimitCents: null,
          budgetStatus: null,
          sessions: [],
          usage: {
            totalCents: 0,
            byType: {},
            byModel: {},
            totalCalls: 0,
            totalTokensIn: 0,
            totalTokensOut: 0,
          },
          spendPolicy: null,
        };
      }
    } catch (err) {
      console.error("[SpendTracker] Failed to load data:", err);
      this.data = {
        totalCostUsd: 0,
        budgetLimitCents: null,
        budgetStatus: null,
        sessions: [],
        usage: {
          totalCents: 0,
          byType: {},
          byModel: {},
          totalCalls: 0,
          totalTokensIn: 0,
          totalTokensOut: 0,
        },
        spendPolicy: null,
      };
    } finally {
      this.loading = false;
    }
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="loading">Loading spend data...</div>
      `;
    }

    if (!this.data) {
      return html`
        <div class="empty-state">No spend data available yet. Start chatting!</div>
      `;
    }

    if (this.mode === "compact") {
      return this.renderCompact();
    }

    return this.renderDashboard();
  }

  private renderCompact() {
    return html`
      <div style="display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2);">
        <span style="font-size: var(--font-xs); color: var(--text-tertiary);">💰 Session:</span>
        <span style="font-size: var(--font-sm); font-weight: 600; color: var(--rax-yellow);">
          $${this.data!.totalCostUsd.toFixed(4)}
        </span>
      </div>
    `;
  }

  private renderDashboard() {
    const data = this.data!;
    const totalCents = data.usage?.totalCents ?? Math.round(data.totalCostUsd * 100);
    const budgetCents = data.budgetLimitCents;
    const budgetPct = budgetCents ? Math.min(100, (totalCents / budgetCents) * 100) : 0;
    const budgetClass = budgetPct > 90 ? "danger" : budgetPct > 70 ? "warning" : "safe";
    const alertThresholdPct = data.spendPolicy
      ? Math.round(data.spendPolicy.alertThreshold * 100)
      : null;
    const overAlertThreshold =
      budgetCents !== null && alertThresholdPct !== null && budgetPct >= alertThresholdPct;
    const overBudgetCap = budgetCents !== null && totalCents >= budgetCents;
    const llmCalls = data.sessions.reduce((sum, s) => sum + s.llmCalls, 0);
    const toolCalls = data.sessions.reduce((sum, s) => sum + s.toolCalls, 0);
    const usageIsEmpty = (data.usage?.totalCalls ?? 0) === 0;

    return html`
      <div class="spend-container">
        <div class="spend-header">
          <div class="spend-title">💰 Spend Tracking</div>
          <div style="display: flex; align-items: center; gap: var(--space-3);">
            ${this.renderDataProvenance(data)}
            <button class="refresh-btn" @click=${() => this.loadData()}>🔄 Refresh</button>
          </div>
        </div>
        ${
          data.usageSource === "runtime_fallback" && data.usageWarning
            ? html`<div class="budget-alert warning">${data.usageWarning}</div>`
            : ""
        }

        <div class="overview-grid">
          <div class="overview-card">
            <div class="overview-label">This Month</div>
            <div class="overview-value">$${(totalCents / 100).toFixed(2)}</div>
            <div class="overview-subtext">Total spend</div>
          </div>
          <div class="overview-card">
            <div class="overview-label">Today</div>
            <div class="overview-value ${data.spendPolicy?.dailyAlertEnabled && data.spendPolicy?.dailySpendCents !== undefined && data.spendPolicy.dailySpendCents >= data.spendPolicy.dailyAlertLimitCents ? "red" : ""}">${data.spendPolicy?.dailySpendCents !== undefined ? `$${(data.spendPolicy.dailySpendCents / 100).toFixed(2)}` : "$0.00"}</div>
            <div class="overview-subtext">${data.spendPolicy?.dailyAlertEnabled ? `Limit: $${(data.spendPolicy.dailyAlertLimitCents / 100).toFixed(2)}` : "Daily spend"}</div>
          </div>
          <div class="overview-card">
            <div class="overview-label">LLM Calls</div>
            <div class="overview-value green">${llmCalls}</div>
            <div class="overview-subtext">AI model calls</div>
          </div>
          <div class="overview-card">
            <div class="overview-label">Tool Calls</div>
            <div class="overview-value green">${toolCalls}</div>
            <div class="overview-subtext">Tool executions</div>
          </div>
          <div class="overview-card">
            <div class="overview-label">Active Sessions</div>
            <div class="overview-value">${data.sessions.length}</div>
            <div class="overview-subtext">Chat sessions</div>
          </div>
        </div>

        ${
          budgetCents
            ? html`
          <div class="budget-section">
            <div class="budget-label">
              <span>Budget Usage</span>
              <span>$${(totalCents / 100).toFixed(2)} / $${(budgetCents / 100).toFixed(2)}</span>
            </div>
            <div class="budget-bar">
              <div class="budget-fill ${budgetClass}" style="width: ${budgetPct}%"></div>
            </div>
            ${
              overAlertThreshold
                ? html`
                  <div class="budget-alert ${overBudgetCap ? "danger" : "warning"}">
                    ${
                      overBudgetCap
                        ? data.spendPolicy?.pauseOnCap
                          ? "Budget cap reached. New chat turns are blocked and pause-on-cap can pause active agents until you resume."
                          : "Budget cap reached. New chat turns will be blocked until you raise your cap or wait for the next cycle."
                        : `Spend alert: you crossed your ${alertThresholdPct}% threshold.`
                    }
                  </div>
                `
                : ""
            }
          </div>
        `
            : ""
        }

        ${this.renderPolicySection(data)}

        <div style="margin-bottom: var(--space-6);">
          <rax-cost-trend-chart></rax-cost-trend-chart>
        </div>

        ${this.renderTokenBreakdown(data)}

        ${
          data.usage?.byModel && Object.keys(data.usage.byModel).length > 0
            ? html`
          <div class="breakdown-section">
            <div class="breakdown-title">By Model</div>
            ${Object.entries(data.usage.byModel)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(
                ([model, cents]) => html`
                <div class="breakdown-item">
                  <span class="breakdown-name">${model}</span>
                  <span class="breakdown-cost">$${((cents as number) / 100).toFixed(2)}</span>
                </div>
              `,
              )}
          </div>
        `
            : ""
        }

        ${
          data.usage?.bySkill && Object.keys(data.usage.bySkill).length > 0
            ? html`
          <div class="breakdown-section">
            <div class="breakdown-title">By Skill</div>
            ${Object.entries(data.usage.bySkill)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(
                ([skill, cents]) => html`
                <div class="breakdown-item">
                  <span class="breakdown-name">${skill}</span>
                  <span class="breakdown-cost">$${((cents as number) / 100).toFixed(2)}</span>
                </div>
              `,
              )}
          </div>
        `
            : ""
        }

        ${
          data.sessions.length > 0
            ? html`
          <div class="sessions-title">Recent Sessions</div>
          ${data.sessions.slice(0, 10).map(
            (session) => html`
            <div class="session-item">
              <span class="session-key">${session.sessionKey.slice(0, 20)}...</span>
              <div class="session-stats">
                <span class="session-stat">${session.llmCalls} LLM</span>
                <span class="session-stat">${session.toolCalls} tools</span>
                <span class="session-cost">$${session.totalCostUsd.toFixed(4)}</span>
              </div>
            </div>
          `,
          )}
        `
            : html`
          <div class="empty-state">
            <p>${
              usageIsEmpty
                ? "No metered usage yet. Send a chat turn, then check Audit → Start Monitoring if totals stay empty."
                : "No sessions yet. Start chatting to see spend data!"
            }</p>
          </div>
        `
        }
      </div>
    `;
  }

  private renderPolicySection(data: SpendData) {
    if (!data.spendPolicy) {
      return "";
    }

    const policy = data.spendPolicy;
    return html`
      <div class="policy-section">
        <div class="policy-title">Safety Cap Settings</div>
        <div class="policy-note">
          Effective cap = min(tier limit and your safety cap). Default cap is
          $${(policy.defaultLowCapCents / 100).toFixed(2)} until you explicitly opt up.
        </div>
        <div class="policy-grid">
          <div class="policy-field">
            <label>Monthly safety cap (USD)</label>
            <input
              type="number"
              min="0.25"
              step="0.25"
              .value=${this.capUsdInput}
              @input=${(event: Event) => {
                this.capUsdInput = (event.target as HTMLInputElement).value;
              }}
            />
          </div>
          <div class="policy-field">
            <label>Alert threshold (%)</label>
            <input
              type="number"
              min="50"
              max="95"
              step="1"
              .value=${String(this.alertPercentInput)}
              @input=${(event: Event) => {
                this.alertPercentInput = Number((event.target as HTMLInputElement).value);
              }}
            />
          </div>
        </div>
        <label class="policy-checkbox">
          <input
            type="checkbox"
            .checked=${this.explicitOptUpInput}
            @change=${(event: Event) => {
              this.explicitOptUpInput = (event.target as HTMLInputElement).checked;
            }}
          />
          I explicitly approve spend caps above the default low cap.
        </label>
        <label class="policy-checkbox">
          <input
            type="checkbox"
            .checked=${this.pauseOnCapInput}
            @change=${(event: Event) => {
              this.pauseOnCapInput = (event.target as HTMLInputElement).checked;
            }}
          />
          Pause active agents when monthly cap is reached (resume manually).
        </label>

        <!-- Daily Spend Alerts Section -->
        <div style="margin-top: var(--space-4); padding-top: var(--space-4); border-top: 1px solid var(--border-color);">
          <div class="policy-title" style="font-size: var(--font-sm); margin-bottom: var(--space-2);">Daily Spend Alerts</div>
          <label class="policy-checkbox">
            <input
              type="checkbox"
              .checked=${this.dailyAlertEnabledInput}
              @change=${(event: Event) => {
                this.dailyAlertEnabledInput = (event.target as HTMLInputElement).checked;
              }}
            />
            Enable daily spend alerts
          </label>
          ${
            this.dailyAlertEnabledInput
              ? html`
            <div class="policy-grid" style="margin-top: var(--space-2);">
              <div class="policy-field">
                <label>Daily limit (USD)</label>
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  .value=${this.dailyAlertLimitUsdInput}
                  @input=${(event: Event) => {
                    this.dailyAlertLimitUsdInput = (event.target as HTMLInputElement).value;
                  }}
                />
              </div>
            </div>
            <label class="policy-checkbox">
              <input
                type="checkbox"
                .checked=${this.dailyAlertEmailInput}
                @change=${(event: Event) => {
                  this.dailyAlertEmailInput = (event.target as HTMLInputElement).checked;
                }}
              />
              Send email notification when daily limit is exceeded
            </label>
            ${
              policy.dailySpendCents !== undefined
                ? html`
              <div class="policy-note" style="margin-top: var(--space-2);">
                Today's spend: $${(policy.dailySpendCents / 100).toFixed(2)} / $${this.dailyAlertLimitUsdInput}
                ${
                  policy.dailySpendCents >= policy.dailyAlertLimitCents
                    ? html`
                        <span style="color: var(--error); margin-left: var(--space-2)">(limit exceeded)</span>
                      `
                    : ""
                }
              </div>
            `
                : ""
            }
          `
              : ""
          }
        </div>

        <div class="policy-actions">
          <div class="policy-note">
            Tier cap: $${(policy.tierLimitCents / 100).toFixed(2)} · Effective:
            $${(policy.effectiveLimitCents / 100).toFixed(2)} · Remaining:
            $${(policy.remainingCents / 100).toFixed(2)}
          </div>
          <div style="display: flex; align-items: center; gap: var(--space-2);">
            ${
              Array.isArray(policy.autoPausedAgentIds) && policy.autoPausedAgentIds.length > 0
                ? html`
                  <button
                    class="secondary-btn"
                    ?disabled=${this.resumingPausedAgents}
                    @click=${() => this.resumePausedAgents()}
                  >
                    ${
                      this.resumingPausedAgents
                        ? "Resuming..."
                        : `Resume paused agents (${policy.autoPausedAgentIds.length})`
                    }
                  </button>
                `
                : ""
            }
            <button
              class="save-btn"
              ?disabled=${this.savingPolicy}
              @click=${() => this.saveSpendPolicy()}
            >
              ${this.savingPolicy ? "Saving..." : "Save Cap"}
            </button>
          </div>
        </div>
        ${this.policyError ? html`<div class="policy-error">${this.policyError}</div>` : ""}
      </div>
    `;
  }

  private async saveSpendPolicy() {
    const monthlyHardCapUsd = Number(this.capUsdInput);
    if (!Number.isFinite(monthlyHardCapUsd) || monthlyHardCapUsd <= 0) {
      this.policyError = "Enter a valid monthly cap in USD.";
      return;
    }

    const alertPercent = Number(this.alertPercentInput);
    if (!Number.isFinite(alertPercent) || alertPercent < 50 || alertPercent > 95) {
      this.policyError = "Alert threshold must be between 50 and 95.";
      return;
    }

    const dailyAlertLimitUsd = Number(this.dailyAlertLimitUsdInput);
    if (
      this.dailyAlertEnabledInput &&
      (!Number.isFinite(dailyAlertLimitUsd) || dailyAlertLimitUsd <= 0)
    ) {
      this.policyError = "Enter a valid daily alert limit in USD.";
      return;
    }

    this.savingPolicy = true;
    this.policyError = null;

    try {
      const response = await raxApi.updateSpendPreferences({
        monthlyHardCapCents: Math.round(monthlyHardCapUsd * 100),
        alertThreshold: alertPercent / 100,
        explicitOptUp: this.explicitOptUpInput,
        pauseOnCap: this.pauseOnCapInput,
        dailyAlertEnabled: this.dailyAlertEnabledInput,
        dailyAlertLimitCents: Math.round(dailyAlertLimitUsd * 100),
        dailyAlertEmail: this.dailyAlertEmailInput,
      });
      if (!response.success) {
        this.policyError = response.error || "Failed to update spend settings.";
        return;
      }
      await this.loadData();
    } catch (error) {
      this.policyError =
        error instanceof Error ? error.message : "Failed to update spend settings.";
    } finally {
      this.savingPolicy = false;
    }
  }

  private async resumePausedAgents() {
    if (this.resumingPausedAgents) return;
    this.resumingPausedAgents = true;
    this.policyError = null;
    try {
      const response = await raxApi.resumeSpendPausedAgents();
      if (!response.success) {
        this.policyError = response.error || "Failed to resume paused agents.";
        return;
      }
      await this.loadData();
    } catch (error) {
      this.policyError = error instanceof Error ? error.message : "Failed to resume paused agents.";
    } finally {
      this.resumingPausedAgents = false;
    }
  }

  /** Show data provenance so users know where spend data comes from */
  private renderDataProvenance(data: SpendData) {
    const source = data.usageSource === "runtime_fallback" ? "runtime" : "metering API";
    const icon = data.usageSource === "runtime_fallback" ? "⚡" : "💵";
    const lastSync = data.lastSyncAt
      ? this.formatRelativeTime(new Date(data.lastSyncAt))
      : "just now";

    return html`
      <span style="
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      ">
        ${icon} Live from ${source} • Updated ${lastSync}
      </span>
    `;
  }

  /** Token breakdown showing input/output/thinking costs */
  private renderTokenBreakdown(data: SpendData) {
    const usage = data.usage;
    if (!usage) return "";

    // Calculate token costs if not provided
    const inputCents = usage.inputCostCents ?? 0;
    const outputCents = usage.outputCostCents ?? 0;
    const thinkingCents = usage.thinkingCostCents ?? 0;
    const totalTokenCents = inputCents + outputCents + thinkingCents;

    // Only show if we have token data
    if (totalTokenCents === 0 && usage.totalTokensIn === 0 && usage.totalTokensOut === 0) {
      return "";
    }

    return html`
      <div class="breakdown-section">
        <div class="breakdown-title">By Token Type</div>
        <div class="breakdown-item">
          <span class="breakdown-name">
            📥 Input Tokens
            <span style="color: var(--text-tertiary); font-size: var(--font-xs); margin-left: var(--space-2);">
              ${this.formatTokenCount(usage.totalTokensIn ?? 0)}
            </span>
          </span>
          <span class="breakdown-cost">$${(inputCents / 100).toFixed(2)}</span>
        </div>
        <div class="breakdown-item">
          <span class="breakdown-name">
            📤 Output Tokens
            <span style="color: var(--text-tertiary); font-size: var(--font-xs); margin-left: var(--space-2);">
              ${this.formatTokenCount(usage.totalTokensOut ?? 0)}
            </span>
          </span>
          <span class="breakdown-cost">$${(outputCents / 100).toFixed(2)}</span>
        </div>
        ${
          (usage.totalTokensThinking ?? 0) > 0 || thinkingCents > 0
            ? html`
          <div class="breakdown-item">
            <span class="breakdown-name">
              🧠 Thinking Tokens
              <span style="color: var(--text-tertiary); font-size: var(--font-xs); margin-left: var(--space-2);">
                ${this.formatTokenCount(usage.totalTokensThinking ?? 0)}
              </span>
            </span>
            <span class="breakdown-cost">$${(thinkingCents / 100).toFixed(2)}</span>
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  /** Format token count with K/M suffixes */
  private formatTokenCount(count: number): string {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
  }

  /** Format relative time like "2 min ago" */
  private formatRelativeTime(date: Date): string {
    const now = Date.now();
    const diff = now - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-spend-tracker": RaxSpendTracker;
  }
}
